<template>
  <v-app>
    <!-- NAV DRAWER -->
    <v-navigation-drawer
      v-model="drawer"
      app
      :permanent="mdAndUp"
      :temporary="!mdAndUp"
      width="260"
      class="elevation-2"
      style="background-color:#ffffff; border-right:1px solid #eee;"
    >
      <v-list density="compact">
        <div class="pl-4">
          <h3 class="font-weight-bold text-primary mb-2">Menu</h3>
        </div>

        <v-divider class="my-3" />

        <!-- Home -->
        <v-list-item
          title="Home"
          rounded
          @click="goHome"
          class="font-weight-medium"
        >
          <template #append>
            <v-icon color="grey">mdi-home</v-icon>
          </template>
        </v-list-item>

        <v-divider class="my-2" />

        <!-- Steps -->
        <v-list-item
          v-for="item in steps"
          :key="item.step"
          :title="item.label"
          rounded
          @click="selectStep(item.step)"
          :disabled="item.disabled"
          :class="['menu-item', { active: step === item.step, disabled: item.disabled }]"
        >
          <template #append>
            <v-icon :color="step === item.step ? 'primary' : 'grey'">
              {{ item.icon }}
            </v-icon>
          </template>
        </v-list-item>

        <!-- LINKS EXTRAS -->
        <v-divider
          v-if="noticeUrl || criteriaUrl"
          class="my-3"
        />

        <v-list-item
          v-if="noticeUrl"
          title="Edital"
          rounded
          @click="openExternal(noticeUrl)"
        >
          <template #append>
            <v-icon color="grey">mdi-file-document</v-icon>
          </template>
        </v-list-item>

        <v-list-item
          v-if="criteriaUrl"
          title="Critérios de Avaliação"
          rounded
          @click="openExternal(criteriaUrl)"
        >
          <template #append>
            <v-icon color="grey">mdi-format-list-bulleted</v-icon>
          </template>
        </v-list-item>
      </v-list>
    </v-navigation-drawer>

    <!-- APPBAR MOBILE -->
    <v-app-bar v-if="!mdAndUp" color="primary" app density="comfortable">
      <v-app-bar-nav-icon @click="drawer = !drawer" />
      <v-toolbar-title class="text-white">{{ currentStepLabel }}</v-toolbar-title>
    </v-app-bar>

    <!-- CONTEÚDO -->
    <v-main>
      <v-container fluid class="pa-4 pb-12">
        <v-row justify="center">
          <v-col cols="12">

            <v-alert
              v-if="sessionExpired"
              type="error"
              class="mb-6"
              border="left"
              colored-border
              elevation="2"
            >
              Sua sessão expirou. Faça login novamente.
              <v-btn color="primary" class="ml-4" @click="redirectToLogin">
                Ir para login
              </v-btn>
            </v-alert>

            <component
              v-if="step === 1 && !sessionExpired"
              :is="'ApplicationFormPersonal'"
              :modelValue="personalData"
              @update:modelValue="personalData = $event"
              @next="goToNextStep"
            />

            <component
              v-else-if="step === 2 && !sessionExpired"
              :is="inscriptionFormComponent"
              :processId="processId"
              :type="type"
              @back="goToPreviousStep"
              @next="goToFinalizeStep"
            />

            <component
              v-else-if="step === 3 && !sessionExpired"
              :is="'ApplicationSummary'"
              :personalData="personalData"
              :inscriptionData="inscriptionData"
              :processId="processId"
              :type="type"
              @back="goToPreviousStep"
              @edit-personal="goToPersonalStep"
              @submit="submitApplication"
            />

          </v-col>
        </v-row>
      </v-container>
    </v-main>
  </v-app>

  <v-card
    class="help-card"
    elevation="6"
    @click="showContacts = true"
  >
    <v-icon
      size="28"
      class="mr-2"
    >
      mdi-account-question
    </v-icon>
    <span class="text-subtitle-1 font-weight-medium">
      Precisa de ajuda?
    </span>
  </v-card>
    <v-dialog
    v-model="showContacts"
    max-width="500"
  >
    <v-card
      rounded="lg"
      elevation="8"
    >
      <v-card-title class="d-flex align-center bg-secondary mb-4 px-6">
        <v-icon
          size="24"
          class="mr-2"
        >
          mdi-account-question
        </v-icon>
        Precisa de ajuda?
      </v-card-title>
      <v-card-text class="pa-6">
        <div class="text-body-1 mb-4">
          Entre em contato com a equipe responsável:
        </div>
        <v-list density="compact">
          <v-list-item>
            <template #prepend>
              <v-icon color="primary">
                mdi-email-outline
              </v-icon>
            </template>
            <v-list-item-title>
              Dúvidas e informações
            </v-list-item-title>
            <v-list-item-subtitle>
              <a href="mailto:educacao@unicamp.br">
                educacao@unicamp.br
              </a>
            </v-list-item-subtitle>
          </v-list-item>
          <v-list-item>
            <template #prepend>
              <v-icon color="primary">
                mdi-email-outline
              </v-icon>
            </template>
            <v-list-item-title>
              Problemas Técnicos
            </v-list-item-title>
            <v-list-item-subtitle>
              <a href="mailto:ticfe@unicamp.br">
                ticfe@unicamp.br
              </a>
            </v-list-item-subtitle>
          </v-list-item>
        </v-list>
      </v-card-text>
      <v-card-actions class="px-6 pb-5">
        <v-spacer />
        <v-btn
          variant="text"
          color="primary"
          @click="showContacts = false"
        >
          Fechar
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import axios from 'axios'
import { useDisplay } from 'vuetify'
import { watch } from 'vue'
import keycloak from '@/plugins/keycloak'

import ApplicationFormPersonal from './forms/ApplicationFormPersonal.vue'
import ApplicationFormRegular from './forms/ApplicationFormRegular.vue'
import ApplicationFormSpecial from './forms/ApplicationFormSpecial.vue'
import ApplicationSummary from './forms/ApplicationSummary.vue'
import { getRegistrationStatus } from '@/utils/registration';

export default {
  name: 'ApplicationWizard',
  // props: ['type', 'id'],

  components: {
    ApplicationFormPersonal,
    ApplicationFormRegular,
    ApplicationFormSpecial,
    ApplicationSummary
  },

  data() {
    return {
      drawer: true,
      step: 1,
      personalData: {},
      inscriptionData: {},
      processId: parseInt(this.$store.state.processId),
      processStatus: "",
      mdAndUp: true,
      sessionExpired: false,

      processData: null,
      noticeUrl: null,
      criteriaUrl: null,
      type: null,
      id: null,

      userId: null,
      existingApplication: null,

      steps: [
        { step: 1, label: 'Dados Pessoais', icon: 'mdi-account', disabled: false },
        { step: 2, label: 'Inscrição', icon: 'mdi-file-document', disabled: true },
        { step: 3, label: 'Resumo', icon: 'mdi-send', disabled: true }
      ],
      form: {
        full_name: '',
        primary_email: '',
      },
      showContacts: false,
      summary: false,
    }
  },

  async mounted() {
    this.summary = this.$route.query.summary === 'true';
    const display = useDisplay()
    this.mdAndUp = !!display.mdAndUp?.value
    this.id = this.$store.state.processId ? this.$store.state.processId : localStorage.getItem('processId')
    this.processId = parseInt(this.id)
    this.type = this.$store.state.type ? this.$store.state.type : localStorage.getItem('type')

    watch(
      () => display.mdAndUp && display.mdAndUp.value,
      (val) => {
        this.mdAndUp = !!val
        this.drawer = !!val
      }
    )
    /*
    if (!keycloak.authenticated || keycloak.isTokenExpired()) {
      this.sessionExpired = true
    }
    */
    if (this.summary) {
      this.step=3;
      this.$store.commit('setDownloadPdf', true);
    }
    await this.fetchProcess();
    if (this.processStatus != 'started') {
      this.steps[0].disabled = true;
      this.steps[1].disabled = true;
      this.step=3;
    }
    await this.loadExistingApplication();
    this.applyExistingApplicationState();
    this.form.full_name = this.fullName;
    this.form.primary_email = this.primaryEmail;
    this.form.id = this.userId;
    if (this.$route.query.update === 'true') {
      await this.updateProfile();
    }
  },

  computed: {
    currentStepLabel() {
      const cur = this.steps.find(s => s.step === this.step)
      return cur ? cur.label : ''
    },

    inscriptionFormComponent() {
      if (this.type === 'special') return 'ApplicationFormSpecial'
      return 'ApplicationFormRegular'
    },

    fullName() {
      return `${keycloak.idTokenParsed?.given_name || ''} ${keycloak.idTokenParsed?.family_name || ''}`.trim();
    },

    primaryEmail() {
      return keycloak.idTokenParsed?.email || '';
    },

  },

  methods: {

    async fetchProcess() {
      try {
        const actives = this.$store.state.activeProcesses;
        const process = actives[this.processId];
        if (!process) return
        this.processStatus = getRegistrationStatus(process.phases);
        this.processData = process
        this.noticeUrl = process.application_form?.notice_url
        this.criteriaUrl = process.application_form?.criteria_url
      } catch (err) {
        console.error('Erro ao carregar processo', err)
      }
    },

    openExternal(url) {
      if (!url) return
      window.open(url, '_blank')
    },

    redirectToLogin() {
      keycloak.login()
    },

    goHome() {
      this.$router.push('/')
      if (!this.mdAndUp) this.drawer = false
    },

    async loadExistingApplication() {
      try {
        const uuid = keycloak?.tokenParsed?.sub;
        if (!uuid) return;

        const profileRes = await axios.get(`${this.$PROFPATH}/profiles`, {
          params: {
            filters: {
              kc_uuid: uuid,
            },
          },
        });
        this.userId = profileRes.data?.data?.[0]?.id || null;
        if (!this.userId) return;

        // Tenta carregar inscrição regular ou especial conforme o tipo atual
        const endpoint = this.type === 'special' ? 'special-applications' : 'regular-applications';
        const appRes = await axios.get(`${this.$BACKPATH}/${endpoint}`, {
          params: {
            'filters[user_id]': this.userId,
            'filters[process_id]': this.processId,
          },
          headers: { Authorization: `Bearer ${keycloak.token}` },
        });

        const app = appRes.data?.data?.[0];
        if (app) {
          this.existingApplication = app;
          this.inscriptionData = { ...app };
        } else {
          // Fallback para casos em que o backend não fornece filtro por process_id
          this.existingApplication = this.loadExistingApplicationFromLocalStorage();
          if (this.existingApplication) {
            this.inscriptionData = { ...this.existingApplication };
          }
        }
      } catch (err) {
        console.warn('Erro ao carregar inscrição existente:', err);
        this.existingApplication = this.loadExistingApplicationFromLocalStorage();
        if (this.existingApplication) {
          this.inscriptionData = { ...this.existingApplication };
        }
      }
    },

    loadExistingApplicationFromLocalStorage() {
      if (!this.userId || !this.processId) return null;

      const regularKey = `regular-application-${this.userId}-${this.processId}`;
      const specialKey = `special-application-${this.userId}-${this.processId}`;

      const regular = localStorage.getItem(regularKey);
      if (regular) return JSON.parse(regular);

      const special = localStorage.getItem(specialKey);
      if (special) return JSON.parse(special);

      return null;
    },

    applyExistingApplicationState() {
      if (!this.existingApplication || this.processStatus !== 'started') return;
      // Permite acesso direto aos passos de inscrição e resumo
      this.steps[1].disabled = false;
      this.steps[2].disabled = false;
    },

    goToNextStep() {
      this.step = 2
      this.steps[1].disabled = false
      if (!this.mdAndUp) this.drawer = false
    },

    goToPreviousStep() {
      if (this.step === 3) this.step = 2
      else if (this.step === 2) this.step = 1
      if (!this.mdAndUp) this.drawer = false
    },

    goToPersonalStep() {
      this.step = 1
      if (!this.mdAndUp) this.drawer = false
    },

    goToFinalizeStep(inscriptionForm) {
      this.inscriptionData = { ...inscriptionForm }
      this.step = 3
      this.steps[2].disabled = false
      if (!this.mdAndUp) this.drawer = false
    },

    selectStep(s) {
      const item = this.steps.find(i => i.step === s)
      if (!item || item.disabled) return
      this.step = s
      if (!this.mdAndUp) this.drawer = false
    },

    submitApplication() {
      this.sessionExpired = true;
      return;
    },
    async updateProfile() {
      let response;
      response=await this.$axios.patch(`${this.$PROFPATH}/profiles/${this.form.id}`,this.form);
      if(response.status !== 200){
        const errorStr = JSON.stringify(response.data.errors);
        throw new Error('Erro na API', errorStr);
      }
      this.$router.push('/form');
    },
    /*
    getRegistrationStatus(phases) {
      for (let i=0; i<phases.length; i++) {
        if (phases[i].type === "registration") {
          const finalDate = new Date(phases[i].end);
          const now = new Date();
          if (now > finalDate) {
            return 'closed'
          }
        }
      }
      return 'started';
    },
    */
  }
}
</script>

<style scoped>
.menu-item {
  transition: background 0.2s, color 0.2s;
  border-radius: 10px;
  margin: 4px 8px;
}

.menu-item:hover {
  background-color: #f5f5f5;
}

.menu-item.active {
  background-color: #e3f2fd;
}

.menu-item.disabled {
  opacity: 0.5;
  pointer-events: none;
}

.help-card {
  position: fixed;
  bottom: 44px;
  right: 24px;
  display: flex;
  align-items: center;
  padding: 12px 20px;
  border-radius: 30px;
  cursor: pointer;
  z-index: 9999;
}
</style>
